/**
 * Spinner Component - Centralized Loading Spinner
 *
 * Single source of truth for all loading spinners in the application.
 * Replaces inconsistent spinner implementations across 15+ CSS files.
 *
 * @example
 * // Small inline spinner (button loading)
 * <Spinner size="xs" />
 *
 * @example
 * // Default spinner (modals, components)
 * <Spinner size="md" />
 *
 * @example
 * // Centered with text (page sections)
 * <Spinner size="lg" text="Loading data..." centered />
 *
 * @param {Object} props
 * @param {string} props.size - Size variant: 'xs' | 'sm' | 'md' | 'lg' (default: 'md')
 * @param {string} props.text - Optional loading text (visible when centered, screen-reader only otherwise)
 * @param {boolean} props.centered - Center the spinner in a container with text below
 *
 * @returns {JSX.Element} Spinner component
 */

import React from 'react';
import PropTypes from 'prop-types';
import '../styles/Spinners.css';

const Spinner = ({ size = 'md', text = null, centered = false }) => {
  // Build CSS classes based on size
  const sizeClass = size === 'md' ? 'spinner' : `spinner spinner-${size}`;

  // Centered layout with visible text
  if (centered) {
    return (
      <div className="spinner-container">
        <div
          className={sizeClass}
          role="status"
          aria-label="Loading"
        />
        {text && <p className="spinner-text">{text}</p>}
      </div>
    );
  }

  // Inline spinner with screen-reader only text
  return (
    <div
      className={sizeClass}
      role="status"
      aria-label="Loading"
    >
      {text && <span className="sr-only">{text}</span>}
    </div>
  );
};

Spinner.propTypes = {
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  text: PropTypes.string,
  centered: PropTypes.bool
};

Spinner.defaultProps = {
  size: 'md',
  text: null,
  centered: false
};

export default Spinner;
